import React, { useState, useEffect, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { CiSearch } from "react-icons/ci";
import { FaFileDownload } from "react-icons/fa";
import { getOperators, getAppointmentReport } from "actions/Monitoring";
import DataTable from "components/DataTable";
import AppoinmentColumns from "utils/columns/AppointmentReport";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { getCurrentDate, notify } from "utils";
import { crearExcel } from "utils/Excel";
import { columnsAppoinmentHistoryExcel } from "utils/columns/columnsExcel";
import { format } from "date-fns";
import Loader from "components/UI/Loader";

const URL_BUCKET = process.env.REACT_APP_BUCKET_BASE_URL ?? "";
export default function AppointmentReport() {
  const { currentProject } = useSelector((state) => state.auth);
  const [t] = useTranslation("monitoring");
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [operators, setOperators] = useState([]);
  const [filter, setFilter] = useState({
    idOperator: null,
    dateInit: "",
    dateEnd: "",
  });

  const getParams = useCallback(() => {
    const params = Object.entries(filter)
      .filter(([_, value]) => value !== null && value !== undefined && value !== "" && value !== "0")
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join("&");

    return params ? `?${params}` : "";
  }, [filter]);

  const validateDates = (dateInit, dateEnd) => {
    if (new Date(dateInit) > new Date(dateEnd)) {
      notify("warning", t("alerts.error_invalid_date_range"), 4000);
      return false;
    }
    return true;
  };

  const getData = async (params = getParams()) => {
    if (!validateDates(filter.dateInit, filter.dateEnd)) return;
    setIsLoading(true)
    const response = await getAppointmentReport(params);
    setIsLoading(false)
    setData(response ?? []);
  }

  const getUrlEvidence = (evidence) => {
    if (!evidence) return "";
    if (evidence === "N/A" || evidence === "No" || evidence.toString().trim() === "") return evidence;
    return `${URL_BUCKET}${evidence}`;
  };

  useEffect(() => {
    if (currentProject && currentProject?.id) {
      async function loadData() {
        try {
          let [responseHistory, operatorsList] = await Promise.all([
            getAppointmentReport(""),
            getOperators(currentProject.id)
          ]);

          if (responseHistory?.length > 0) {
            responseHistory = responseHistory.map(item => ({
              ...item,
              evidenceArrival: getUrlEvidence(item.evidenceArrival),
              evidenceEnd: getUrlEvidence(item.evidenceEnd),
            }));
          }
          setData(responseHistory ?? [])
          setOperators(operatorsList ?? [])
        } finally {
          setIsLoading(false)
        }
      }
      loadData();
    }
  }, [currentProject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value,
    });
  };

  const handleSearch = () => {
    if (currentProject && currentProject?.id) {
      getData();
    }
  };

  const getDate = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr === "0001-01-01T00:00:00") return "";

    return format(new Date(dateStr), "dd/MM/yyyy HH:mm:ss")
  }

  const handleCreationExcel = async () => {
    try {
      if (data.length <= 0) {
        notify("warning", "No hay datos", 600)
        return;
      }

      const fileName = `ReporteCitas${getCurrentDate()}.xlsx`;
      const sheetName = "Reporte";

      const formattedData = data.map(info => ({
        ...info,
        newCollectionDate: getDate(info.collectionDate),
        newStatus: info.status,
      }));

      await crearExcel(fileName, sheetName, formattedData, columnsAppoinmentHistoryExcel(t));
    } catch (error) {
      console.error("Error al crear el archivo Excel:", error);
    }
  };

  const table = useReactTable({
    data,
    columns: AppoinmentColumns(t),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="bg-[#f4f3fb] min-h-screen p-6">
      <div className="bg-black flex flex-row justify-between text-white px-6 py-4 rounded-t-xl">
        <h1 className="text-lg font-bold">Reporte de citas</h1>
        <div className="flex flex-wrap justify-end items-center gap-4">
          <div className="flex flex-col">
            <label className="text-sm font-medium">{t("monitoring.operator")}:</label>
            <select
              className="border text-black border-gray-300 rounded px-2 py-1 text-sm min-w-[200px]"
              name="idOperator"
              value={filter.idOperator}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value={0}>Selecciona una opción</option>
              {
                operators.map((item, index) => (
                  <option value={item.id} key={index}> {item.user.name} {item.user.lastName} {item.user.secondLastName} </option>
                ))
              }
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium">{t("monitoring.start_date")}:</label>
            <input
              className="text-sm text-black border border-gray-300 rounded px-2 py-1"
              name="dateInit"
              type="date"
              value={filter.dateInit}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium">{t("monitoring.end_date")}:</label>
            <input
              className="text-sm text-black border border-gray-300 rounded px-2 py-1"
              name="dateEnd"
              type="date"
              value={filter.dateEnd}
              onChange={handleChange}
            />
          </div>
          <button onClick={() => handleSearch()} className="h-8 w-8 bg-white border border-gray-300 py-1 rounded-full hover:bg-gray-100 shadow text-black self-end">
            <CiSearch className="text-2xl self-center justify-self-center" />
          </button>
          <button onClick={() => handleCreationExcel()} className="h-8 w-8 bg-white border border-gray-300 py-1 rounded-full hover:bg-gray-100 shadow text-black self-end">
            <FaFileDownload className="text-2xl self-center justify-self-center" />
          </button>
        </div>
      </div>
      {
        isLoading ? (
          <Loader />
        ) : (
          <div className="lg:h-[75vh] pt-2">
            <DataTable table={table} w="100%" />
          </div>
        )
      }
    </div>
  );
}
